import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export type PeriodFilter = '7d' | '30d' | '90d' | '365d' | 'all';

export interface ExecutiveFilters {
  period: PeriodFilter;
  planId: string | null;
  userStatus: 'all' | 'active' | 'inactive';
}

export interface ExecutiveKPIs {
  totalUsers: number;
  activeUsers: number;
  usersWithTransactions: number;
  totalAccounts: number;
  totalCards: number;
  totalTransactions: number;
  totalActiveGoals: number;
  usersWithCalendar: number;
  usersWithPendingNotifications: number;
}

export interface EngagementMetrics {
  usersActiveInPeriod: number;
  usersWithAtLeastOneTransaction: number;
  usersWithNoRecentActivity: number;
  usersWithActiveGoals: number;
  usersWithCalendarActive: number;
  avgTransactionsPerUser: number;
  avgAccountsPerUser: number;
  avgCardsPerUser: number;
  planDistribution: { planName: string; count: number; pct: number }[];
  activityDistribution: { label: string; count: number }[];
}

export interface FinancialHealthMetrics {
  totalIncome: number;
  totalExpenses: number;
  consolidatedBalance: number;
  totalPending: number;
  totalOverdue: number;
  totalFuture: number;
  pctUsersPositiveBalance: number;
  pctUsersNegativeBalance: number;
  pctUsersWithOverdue: number;
  recurringWeight: number;
  installmentWeight: number;
}

export interface CategoryMetric {
  name: string;
  count: number;
  total: number;
  pct: number;
}

export interface BehaviorMetrics {
  topCategoriesByFrequency: CategoryMetric[];
  topCategoriesByAmount: CategoryMetric[];
  accountVsCardPct: { account: number; card: number };
  monthlyTrend: { month: string; income: number; expenses: number }[];
  pendingByPeriod: { label: string; count: number; total: number }[];
}

export interface DistributionRow {
  label: string;
  count: number;
  pct: number;
}

export interface DistributionMetrics {
  byBalanceRange: DistributionRow[];
  byAccountCount: DistributionRow[];
  byCardCount: DistributionRow[];
  byTransactionCount: DistributionRow[];
  byPendingCount: DistributionRow[];
  byPlanEngagement: { planName: string; avgTx: number; avgGoals: number; pctActive: number }[];
}

export interface ManagerialAlert {
  type: 'danger' | 'warning' | 'info' | 'success';
  icon: string;
  title: string;
  description: string;
  value?: string;
}

export interface ExecutiveData {
  kpis: ExecutiveKPIs;
  engagement: EngagementMetrics;
  financialHealth: FinancialHealthMetrics;
  behavior: BehaviorMetrics;
  distribution: DistributionMetrics;
  alerts: ManagerialAlert[];
  plans: { id: string; name: string }[];
}

const ADMIN_ROLES = ['master_admin', 'admin', 'support', 'finance'];

const getPeriodStart = (period: PeriodFilter): Date | null => {
  if (period === 'all') return null;
  const d = new Date();
  if (period === '7d') d.setDate(d.getDate() - 7);
  else if (period === '30d') d.setDate(d.getDate() - 30);
  else if (period === '90d') d.setDate(d.getDate() - 90);
  else if (period === '365d') d.setFullYear(d.getFullYear() - 1);
  return d;
};

const pct = (part: number, total: number) => (total > 0 ? Math.round((part / total) * 100) : 0);

const distrib = (
  items: number[],
  ranges: { label: string; min: number; max: number }[]
): DistributionRow[] => {
  const total = items.length;
  return ranges.map((r) => {
    const count = items.filter((v) => v >= r.min && v <= r.max).length;
    return { label: r.label, count, pct: pct(count, total) };
  });
};

const DEFAULT_DATA: ExecutiveData = {
  kpis: {
    totalUsers: 0, activeUsers: 0, usersWithTransactions: 0,
    totalAccounts: 0, totalCards: 0, totalTransactions: 0,
    totalActiveGoals: 0, usersWithCalendar: 0, usersWithPendingNotifications: 0,
  },
  engagement: {
    usersActiveInPeriod: 0, usersWithAtLeastOneTransaction: 0,
    usersWithNoRecentActivity: 0, usersWithActiveGoals: 0,
    usersWithCalendarActive: 0, avgTransactionsPerUser: 0,
    avgAccountsPerUser: 0, avgCardsPerUser: 0,
    planDistribution: [], activityDistribution: [],
  },
  financialHealth: {
    totalIncome: 0, totalExpenses: 0, consolidatedBalance: 0,
    totalPending: 0, totalOverdue: 0, totalFuture: 0,
    pctUsersPositiveBalance: 0, pctUsersNegativeBalance: 0,
    pctUsersWithOverdue: 0, recurringWeight: 0, installmentWeight: 0,
  },
  behavior: {
    topCategoriesByFrequency: [], topCategoriesByAmount: [],
    accountVsCardPct: { account: 0, card: 0 },
    monthlyTrend: [], pendingByPeriod: [],
  },
  distribution: {
    byBalanceRange: [], byAccountCount: [], byCardCount: [],
    byTransactionCount: [], byPendingCount: [], byPlanEngagement: [],
  },
  alerts: [],
  plans: [],
};

export const useExecutiveData = (filters: ExecutiveFilters) => {
  const { user } = useAuth();
  const [data, setData] = useState<ExecutiveData>(DEFAULT_DATA);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    // Guard: only fetch if user is authenticated (admin-only view)
    if (!user) { setLoading(false); return; }
    setLoading(true);
    try {
      const periodStart = getPeriodStart(filters.period);

      // Fetch admin IDs to exclude
      const { data: adminRoles } = await supabase
        .from('user_roles')
        .select('user_id')
        .in('role', ADMIN_ROLES);
      const adminIds = new Set((adminRoles || []).map((r: any) => r.user_id));

      // Fetch plans
      const { data: plansData } = await supabase.from('plans').select('id, name');
      const plans = (plansData || []).map((p: any) => ({ id: p.id, name: p.name }));

      // Fetch all profiles (no personal data in aggregation)
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, is_active, is_blocked, created_at')
        .limit(10000);

      if (profilesError) console.error('[ExecutiveData] profiles error:', profilesError);

      const allProfiles = (profilesData || []).filter((p: any) => !adminIds.has(p.id));
      let filteredProfiles = allProfiles;

      // Filter by user status
      if (filters.userStatus === 'active') {
        filteredProfiles = allProfiles.filter((p: any) => p.is_active !== false && !p.is_blocked);
      } else if (filters.userStatus === 'inactive') {
        filteredProfiles = allProfiles.filter((p: any) => p.is_active === false || p.is_blocked);
      }
      const userIds = new Set(filteredProfiles.map((p: any) => p.id));
      const totalUsers = filteredProfiles.length;
      const activeUsers = filteredProfiles.filter((p: any) => p.is_active !== false && !p.is_blocked).length;

      // Fetch subscriptions for plan filter + distribution
      const { data: subsData, error: subsError } = await supabase
        .from('subscriptions')
        .select('user_id, status, subscription_type, plan_id, amount')
        .limit(10000);
      if (subsError) console.error('[ExecutiveData] subscriptions error:', subsError);
      const allSubs = (subsData || []).filter((s: any) => userIds.has(s.user_id));

      // Apply plan filter
      const planFilteredUserIds = filters.planId
        ? new Set(allSubs.filter((s: any) => s.plan_id === filters.planId).map((s: any) => s.user_id))
        : userIds;

      // Fetch accounts
      const { data: accountsData, error: accountsError } = await supabase
        .from('accounts')
        .select('id, user_id, current_balance')
        .limit(10000);
      if (accountsError) console.error('[ExecutiveData] accounts error:', accountsError);
      const allAccounts = (accountsData || []).filter((a: any) => planFilteredUserIds.has(a.user_id));

      // Fetch cards
      const { data: cardsData, error: cardsError } = await supabase
        .from('cards')
        .select('id, user_id')
        .limit(10000);
      if (cardsError) console.error('[ExecutiveData] cards error:', cardsError);
      const allCards = (cardsData || []).filter((c: any) => planFilteredUserIds.has(c.user_id));

      // Fetch transactions (with period filter)
      let txQuery = supabase
        .from('transactions')
        .select('id, user_id, amount, type, status, date, category_id, account_id, card_id, installments, recurrence, transaction_subtype')
        .is('deleted_at', null)
        .limit(50000);
      if (periodStart) txQuery = txQuery.gte('date', periodStart.toISOString().slice(0, 10));
      const { data: txData, error: txError } = await txQuery;
      if (txError) console.error('[ExecutiveData] transactions error:', txError);
      const allTx = (txData || []).filter((t: any) => planFilteredUserIds.has(t.user_id));

      // Fetch last 6 months transactions separately for monthly trend (regardless of period filter)
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 5);
      sixMonthsAgo.setDate(1);
      const { data: trendTxData } = await supabase
        .from('transactions')
        .select('user_id, amount, type, status, date, transaction_subtype')
        .is('deleted_at', null)
        .gte('date', sixMonthsAgo.toISOString().slice(0, 10))
        .limit(50000);
      const trendTx = (trendTxData || []).filter((t: any) => planFilteredUserIds.has(t.user_id));

      console.log('[ExecutiveData] allTx.length:', allTx.length, '| allAccounts:', allAccounts.length, '| allCards:', allCards.length);

      // Fetch goals
      const { data: goalsData, error: goalsError } = await supabase
        .from('goals')
        .select('id, user_id, target_amount, current_amount, status')
        .limit(10000);
      if (goalsError) console.error('[ExecutiveData] goals error:', goalsError);
      const allGoals = (goalsData || []).filter((g: any) => planFilteredUserIds.has(g.user_id));

      // Fetch calendar events
      let calQuery = supabase
        .from('calendar_events')
        .select('id, user_id, start_date')
        .limit(10000);
      if (periodStart) calQuery = calQuery.gte('start_date', periodStart.toISOString().slice(0, 10));
      const { data: calData, error: calError } = await calQuery;
      if (calError) console.error('[ExecutiveData] calendar_events error:', calError);
      const allCal = (calData || []).filter((c: any) => planFilteredUserIds.has(c.user_id));

      // Fetch categories (no user data)
      const { data: catsData } = await supabase.from('categories').select('id, name').limit(10000);
      const catMap: Record<string, string> = {};
      (catsData || []).forEach((c: any) => { catMap[c.id] = c.name; });

      // Fetch pending notifications count (read_at IS NULL = não lida)
      const { data: notifData, error: notifError } = await supabase
        .from('notifications')
        .select('user_id, read_at')
        .is('read_at', null)
        .limit(10000);
      if (notifError) console.error('[ExecutiveData] notifications error:', notifError);
      const allNotifs = (notifData || []).filter((n: any) => planFilteredUserIds.has(n.user_id));

      // ── KPIs ──────────────────────────────────────────────────────────
      const usersWithTx = new Set(allTx.map((t: any) => t.user_id));
      const usersWithGoals = new Set(allGoals.filter((g: any) => g.status === 'active').map((g: any) => g.user_id));
      const usersWithCal = new Set(allCal.map((c: any) => c.user_id));
      const usersWithPendingNotif = new Set(allNotifs.map((n: any) => n.user_id));

      const kpis: ExecutiveKPIs = {
        totalUsers,
        activeUsers,
        usersWithTransactions: usersWithTx.size,
        totalAccounts: allAccounts.length,
        totalCards: allCards.length,
        totalTransactions: allTx.length,
        totalActiveGoals: allGoals.filter((g: any) => g.status === 'active').length,
        usersWithCalendar: usersWithCal.size,
        usersWithPendingNotifications: usersWithPendingNotif.size,
      };

      // ── ENGAGEMENT ────────────────────────────────────────────────────
      // Users with no recent activity = no tx in period
      const usersWithNoActivity = Array.from(planFilteredUserIds).filter(
        (uid) => !usersWithTx.has(uid)
      ).length;

      const usersInPlanIds = Array.from(planFilteredUserIds);
      // CRÍTICO: excluir transferências — não representam receita/despesa real na plataforma
      const nonTransferTx = allTx.filter((t: any) => t.transaction_subtype !== 'transferencia');
      // avgTx: usar nonTransferTx para refletir movimentações financeiras reais
      const avgTx = totalUsers > 0 ? nonTransferTx.length / Math.max(usersWithTx.size, 1) : 0;
      const avgAccounts = totalUsers > 0 ? allAccounts.length / totalUsers : 0;
      const avgCards = totalUsers > 0 ? allCards.length / totalUsers : 0;

      // Plan distribution
      const planCount: Record<string, { name: string; count: number }> = {};
      allSubs.forEach((s: any) => {
        if (!planFilteredUserIds.has(s.user_id)) return;
        const plan = plans.find((p) => p.id === s.plan_id);
        const pName = plan?.name || (s.subscription_type === 'free' ? 'Free' : 'Sem plano');
        if (!planCount[pName]) planCount[pName] = { name: pName, count: 0 };
        planCount[pName].count += 1;
      });
      const planDistribution = Object.values(planCount).map((p) => ({
        planName: p.name,
        count: p.count,
        pct: pct(p.count, totalUsers),
      }));

      // Activity distribution (quartiles)
      const txPerUser: Record<string, number> = {};
      allTx.forEach((t: any) => { txPerUser[t.user_id] = (txPerUser[t.user_id] || 0) + 1; });
      const txCounts = usersInPlanIds.map((uid) => txPerUser[uid] || 0);
      const veryActive = txCounts.filter((c) => c >= 20).length;
      const active = txCounts.filter((c) => c >= 5 && c < 20).length;
      const occasional = txCounts.filter((c) => c >= 1 && c < 5).length;
      const inactive = txCounts.filter((c) => c === 0).length;
      const activityDistribution = [
        { label: 'Muito ativo (≥20)', count: veryActive },
        { label: 'Ativo (5–19)', count: active },
        { label: 'Esporádico (1–4)', count: occasional },
        { label: 'Sem movimento', count: inactive },
      ];

      const engagement: EngagementMetrics = {
        usersActiveInPeriod: usersWithTx.size,
        usersWithAtLeastOneTransaction: usersWithTx.size,
        usersWithNoRecentActivity: usersWithNoActivity,
        usersWithActiveGoals: usersWithGoals.size,
        usersWithCalendarActive: usersWithCal.size,
        avgTransactionsPerUser: avgTx,
        avgAccountsPerUser: avgAccounts,
        avgCardsPerUser: avgCards,
        planDistribution,
        activityDistribution,
      };

      // ── FINANCIAL HEALTH ─────────────────────────────────────────────
      // Status values stored in PT-BR: 'pago', 'recebido', 'pendente'
      // Type values stored in PT-BR: 'receita', 'despesa'
      const paidTx = nonTransferTx.filter((t: any) =>
        t.status === 'pago' || t.status === 'recebido' ||
        t.status === 'paid' || t.status === 'received'  // fallback legacy
      );
      const pendingTx = nonTransferTx.filter((t: any) =>
        t.status === 'pendente' || t.status === 'pending'
      );
      const today = new Date().toISOString().slice(0, 10);
      const overdueTx = pendingTx.filter((t: any) => t.date && t.date < today);
      const futureTx = pendingTx.filter((t: any) => t.date && t.date > today);

      const totalIncome = paidTx.filter((t: any) =>
        t.type === 'receita' || t.type === 'income'
      ).reduce((s: number, t: any) => s + (t.amount || 0), 0);
      const totalExpenses = paidTx.filter((t: any) =>
        t.type === 'despesa' || t.type === 'expense'
      ).reduce((s: number, t: any) => s + (t.amount || 0), 0);
      const consolidatedBalance = totalIncome - totalExpenses;
      const totalPending = pendingTx.reduce((s: number, t: any) => s + (t.amount || 0), 0);
      const totalOverdue = overdueTx.reduce((s: number, t: any) => s + (t.amount || 0), 0);
      const totalFuture = futureTx.reduce((s: number, t: any) => s + (t.amount || 0), 0);

      // Per-user balance
      const incomeByUser: Record<string, number> = {};
      const expenseByUser: Record<string, number> = {};
      const overdueByUser: Record<string, number> = {};
      paidTx.forEach((t: any) => {
        const isIncome = t.type === 'receita' || t.type === 'income';
        if (isIncome) incomeByUser[t.user_id] = (incomeByUser[t.user_id] || 0) + (t.amount || 0);
        else expenseByUser[t.user_id] = (expenseByUser[t.user_id] || 0) + (t.amount || 0);
      });
      overdueTx.forEach((t: any) => { overdueByUser[t.user_id] = (overdueByUser[t.user_id] || 0) + 1; });

      const usersWithBalance = usersInPlanIds.filter(
        (uid) => (incomeByUser[uid] || 0) > 0 || (expenseByUser[uid] || 0) > 0
      );
      const positiveBalance = usersWithBalance.filter(
        (uid) => (incomeByUser[uid] || 0) >= (expenseByUser[uid] || 0)
      ).length;
      const negativeBalance = usersWithBalance.filter(
        (uid) => (incomeByUser[uid] || 0) < (expenseByUser[uid] || 0)
      ).length;
      const usersWithOverdueTx = Object.keys(overdueByUser).length;

      const recurringTx = nonTransferTx.filter((t: any) => t.recurrence && t.recurrence !== 'none');
      const installmentTx = nonTransferTx.filter((t: any) => t.installments && t.installments > 1);
      const txTotal = nonTransferTx.length || 1;

      const financialHealth: FinancialHealthMetrics = {
        totalIncome,
        totalExpenses,
        consolidatedBalance,
        totalPending,
        totalOverdue,
        totalFuture,
        pctUsersPositiveBalance: pct(positiveBalance, Math.max(usersWithBalance.length, 1)),
        pctUsersNegativeBalance: pct(negativeBalance, Math.max(usersWithBalance.length, 1)),
        pctUsersWithOverdue: pct(usersWithOverdueTx, totalUsers),
        recurringWeight: pct(recurringTx.length, txTotal),
        installmentWeight: pct(installmentTx.length, txTotal),
      };

      // ── BEHAVIOR ─────────────────────────────────────────────────────
      // CRÍTICO: usar nonTransferTx para evitar que transferências distorçam
      // as categorias, percentuais de método e peso de recorrências.
      const catFreq: Record<string, { count: number; total: number }> = {};
      nonTransferTx.filter((t: any) => t.type === 'despesa' || t.type === 'expense').forEach((t: any) => {
        const name = catMap[t.category_id] || 'Sem categoria';
        if (!catFreq[name]) catFreq[name] = { count: 0, total: 0 };
        catFreq[name].count += 1;
        catFreq[name].total += t.amount || 0;
      });
      const totalCatTx = Object.values(catFreq).reduce((s, v) => s + v.count, 0) || 1;
      const totalCatAmount = Object.values(catFreq).reduce((s, v) => s + v.total, 0) || 1;
      const catList = Object.entries(catFreq)
        .map(([name, v]) => ({
          name,
          count: v.count,
          total: v.total,
          pct: pct(v.count, totalCatTx),
        }))
        .sort((a, b) => b.count - a.count);

      const txWithAccount = nonTransferTx.filter((t: any) => t.account_id && !t.card_id).length;
      const txWithCard = nonTransferTx.filter((t: any) => t.card_id).length;
      const txWithMethod = txWithAccount + txWithCard || 1;

      // Monthly trend (last 6 months) — uses trendTx (not period-filtered)
      const monthTrend: Record<string, { income: number; expenses: number }> = {};
      for (let i = 5; i >= 0; i--) {
        const d = new Date();
        d.setMonth(d.getMonth() - i);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        monthTrend[key] = { income: 0, expenses: 0 };
      }
      trendTx.forEach((t: any) => {
        if (!t.date) return;
        // CRÍTICO: excluir transferências do trend mensal
        if (t.transaction_subtype === 'transferencia') return;
        const key = t.date.slice(0, 7);
        if (!monthTrend[key]) return;
        const isPaid = t.status === 'pago' || t.status === 'recebido' || t.status === 'paid' || t.status === 'received';
        const isIncome = t.type === 'receita' || t.type === 'income';
        const isExpense = t.type === 'despesa' || t.type === 'expense';
        if (isIncome && isPaid) {
          monthTrend[key].income += t.amount || 0;
        } else if (isExpense && isPaid) {
          monthTrend[key].expenses += t.amount || 0;
        }
      });
      const monthlyTrend = Object.entries(monthTrend).map(([month, v]) => ({
        month,
        income: v.income,
        expenses: v.expenses,
      }));

      // Pending by period bucket
      const pendingByPeriod = [
        { label: 'Vencido', count: overdueTx.length, total: totalOverdue },
        { label: 'Hoje', count: pendingTx.filter((t: any) => t.date === today).length, total: pendingTx.filter((t: any) => t.date === today).reduce((s: number, t: any) => s + (t.amount || 0), 0) },
        { label: 'Próx. 7 dias', count: futureTx.filter((t: any) => { const diff = (new Date(t.date).getTime() - Date.now()) / 86400000; return diff <= 7; }).length, total: futureTx.filter((t: any) => { const diff = (new Date(t.date).getTime() - Date.now()) / 86400000; return diff <= 7; }).reduce((s: number, t: any) => s + (t.amount || 0), 0) },
        { label: 'Futuro', count: futureTx.length, total: totalFuture },
      ];

      const behavior: BehaviorMetrics = {
        topCategoriesByFrequency: catList.slice(0, 7),
        topCategoriesByAmount: [...catList].sort((a, b) => b.total - a.total).slice(0, 7),
        accountVsCardPct: {
          account: pct(txWithAccount, txWithMethod),
          card: pct(txWithCard, txWithMethod),
        },
        monthlyTrend,
        pendingByPeriod,
      };

      // ── DISTRIBUTION ─────────────────────────────────────────────────
      const accountsPerUser = usersInPlanIds.map(
        (uid) => allAccounts.filter((a: any) => a.user_id === uid).length
      );
      const cardsPerUser = usersInPlanIds.map(
        (uid) => allCards.filter((c: any) => c.user_id === uid).length
      );
      const txPerUserArr = usersInPlanIds.map((uid) => txPerUser[uid] || 0);
      const overduePerUser = usersInPlanIds.map((uid) => overdueByUser[uid] || 0);

      // Balance range: use account balances
      const balanceByUser: Record<string, number> = {};
      allAccounts.forEach((a: any) => { balanceByUser[a.user_id] = (balanceByUser[a.user_id] || 0) + (a.current_balance || 0); });
      const balanceArr = usersInPlanIds.map((uid) => balanceByUser[uid] || 0);
      const byBalanceRange = distrib(balanceArr, [
        { label: 'Negativo', min: -Infinity, max: -0.01 },
        { label: 'R$ 0', min: 0, max: 0 },
        { label: 'R$ 0–500', min: 0.01, max: 500 },
        { label: 'R$ 500–2k', min: 500.01, max: 2000 },
        { label: 'R$ 2k–10k', min: 2000.01, max: 10000 },
        { label: 'Acima de R$ 10k', min: 10000.01, max: Infinity },
      ]);
      const byAccountCount = distrib(accountsPerUser, [
        { label: 'Nenhuma', min: 0, max: 0 },
        { label: '1 conta', min: 1, max: 1 },
        { label: '2–3 contas', min: 2, max: 3 },
        { label: '4+ contas', min: 4, max: Infinity },
      ]);
      const byCardCount = distrib(cardsPerUser, [
        { label: 'Nenhum', min: 0, max: 0 },
        { label: '1 cartão', min: 1, max: 1 },
        { label: '2–3 cartões', min: 2, max: 3 },
        { label: '4+ cartões', min: 4, max: Infinity },
      ]);
      const byTransactionCount = distrib(txPerUserArr, [
        { label: 'Nenhum', min: 0, max: 0 },
        { label: '1–4', min: 1, max: 4 },
        { label: '5–19', min: 5, max: 19 },
        { label: '20–49', min: 20, max: 49 },
        { label: '50+', min: 50, max: Infinity },
      ]);
      const byPendingCount = distrib(overduePerUser, [
        { label: 'Sem pendências', min: 0, max: 0 },
        { label: '1–2', min: 1, max: 2 },
        { label: '3–5', min: 3, max: 5 },
        { label: '6+', min: 6, max: Infinity },
      ]);

      // Plan engagement
      const byPlanEngagement = planDistribution.map((p) => {
        const planSubs = allSubs.filter((s: any) => {
          const pObj = plans.find((pl) => pl.id === s.plan_id);
          return (pObj?.name || '') === p.planName && planFilteredUserIds.has(s.user_id);
        });
        const planUserIds = planSubs.map((s: any) => s.user_id);
        const planTx = nonTransferTx.filter((t: any) => planUserIds.includes(t.user_id));
        const planGoals = allGoals.filter((g: any) => planUserIds.includes(g.user_id) && g.status === 'active');
        const planActivePlanUsers = planUserIds.filter((uid: string) => usersWithTx.has(uid)).length;
        return {
          planName: p.planName,
          avgTx: planUserIds.length > 0 ? Math.round(planTx.length / planUserIds.length) : 0,
          avgGoals: planUserIds.length > 0 ? Math.round((planGoals.length / planUserIds.length) * 10) / 10 : 0,
          pctActive: pct(planActivePlanUsers, planUserIds.length),
        };
      });

      const distribution: DistributionMetrics = {
        byBalanceRange,
        byAccountCount,
        byCardCount,
        byTransactionCount,
        byPendingCount,
        byPlanEngagement,
      };

      // ── ALERTS ───────────────────────────────────────────────────────
      const alerts: ManagerialAlert[] = [];
      const pctNegBal = financialHealth.pctUsersNegativeBalance;
      const pctNoActivity = pct(engagement.usersWithNoRecentActivity, totalUsers);
      const pctNoGoals = pct(totalUsers - engagement.usersWithActiveGoals, totalUsers);
      const pctOverdue = financialHealth.pctUsersWithOverdue;

      if (pctNegBal > 30) alerts.push({ type: 'danger', icon: 'ri-arrow-down-circle-line', title: 'Alto índice de saldo negativo', description: `${pctNegBal}% dos usuários com movimentação estão com despesas maiores que receitas.`, value: `${pctNegBal}%` });
      if (pctOverdue > 20) alerts.push({ type: 'warning', icon: 'ri-time-line', title: 'Pendências vencidas elevadas', description: `${pctOverdue}% dos usuários têm ao menos um lançamento vencido.`, value: `${pctOverdue}%` });
      if (pctNoActivity > 50) alerts.push({ type: 'warning', icon: 'ri-user-unfollow-line', title: 'Baixo engajamento no período', description: `${pctNoActivity}% dos usuários não tiveram movimentação no período selecionado.`, value: `${pctNoActivity}%` });
      if (pctNoGoals > 60) alerts.push({ type: 'info', icon: 'ri-flag-line', title: 'Base sem metas ativas', description: `${pctNoGoals}% dos usuários não possuem metas financeiras ativas.`, value: `${pctNoGoals}%` });
      if (financialHealth.recurringWeight > 40) alerts.push({ type: 'info', icon: 'ri-repeat-line', title: 'Alto peso de recorrentes', description: `${financialHealth.recurringWeight}% dos lançamentos são recorrentes — forte dependência de compromissos fixos.`, value: `${financialHealth.recurringWeight}%` });
      if (totalUsers > 0 && engagement.usersActiveInPeriod / totalUsers > 0.6) alerts.push({ type: 'success', icon: 'ri-heart-pulse-line', title: 'Engajamento saudável', description: `${pct(engagement.usersActiveInPeriod, totalUsers)}% da base está ativa no período — acima da média esperada.`, value: `${pct(engagement.usersActiveInPeriod, totalUsers)}%` });
      if (alerts.length === 0) alerts.push({ type: 'info', icon: 'ri-shield-check-line', title: 'Sem alertas críticos', description: 'Nenhum indicador crítico identificado para o período e segmento selecionados.' });

      setData({ kpis, engagement, financialHealth, behavior, distribution, alerts, plans });
    } catch (err) {
      console.error('useExecutiveData error:', err);
    } finally {
      setLoading(false);
    }
  }, [user, filters.period, filters.planId, filters.userStatus]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, refetch: fetchData };
};
